import { inject, Injectable } from '@angular/core';

import { PortfolioFlagsService } from '@ecm/portfolio';
import { KenobiParams, KenobiService, TMSService } from '@ecm/tracker';

import { PushV7 } from '../business/api/dashboardV7/pushV7';
import { SharedServiceEnum } from '../business/api/dashboardV7/sharedServicesEnum';
import { DashboardV7ViewModel } from '../business/model/home/dashboard/dashboardV7.view-model';
import { SectionV7ViewModel } from '../business/model/home/sectionV7.view-model';
import { TmsCodes } from '../business/model/tracking/tmsCodes.enum';

/**
 * Type alias for tracking elements that can be either a SectionV7ViewModel, PushV7, or a string
 */
type TrackingElement = SectionV7ViewModel | PushV7 | string;

/**
 * Interface for contract tracking data
 */
interface DashboardTrackingContract {
    offerType: string;
    offerLabel: string;
    brand: string;
    role: string;
    hasCybersecureOption: boolean;
}

interface DashboardTrackingIdentity {
    isEligibleToBloom: boolean;
}

/**
 * Interface for dashboard tracking data
 */
interface DashboardTrackingData {
    trackingLabel: string;
    contrat: DashboardTrackingContract;
    identity: DashboardTrackingIdentity;
}

@Injectable({
    providedIn: 'root',
})
export class TrackingService {
    protected readonly portfolioFlagsService = inject(PortfolioFlagsService);
    private readonly tmsService = inject(TMSService);
    private readonly kenobiService = inject(KenobiService);

    /**
     * Tracks view in kenobi
     * @param {KenobiParams} kenobiParams Tracking info
     * @param {string} pageId Transaction properties for BCO monitoring
     * @param {boolean}  isVisitor Set to `true` on visitor pages
     * @param {string} cid if is present or "undefined"
     */
    public trackKenobi(kenobiParams: KenobiParams, pageId: string, isVisitor = false, cid?: string): void {
        this.kenobiService.trackMulti(kenobiParams, pageId || 'undefined', isVisitor, cid || undefined);
    }

    /**
     * Tracks view in TMS  for dashboard page
     */
    public trackDashboardView(trackingData: DashboardTrackingData, viewModel?: DashboardV7ViewModel): void {
        const isSosh = this.portfolioFlagsService.isSosh();
        const keys = viewModel?.sharedServices?.map((sharedService) => sharedService.type) || [];
        const viewData: any = {
            type_ec: isSosh ? 'Espace client Sosh' : 'Espace client Orange',
            couleur_contrat: isSosh ? 'Sosh' : 'Orange',
            type_page: 'hp',
            titre_page: trackingData.trackingLabel,
            sous_univers: 'global',
            univers_affichage: 'global',
            type_acte: 'NR',
            id_tracking: 'lapfZ9bm',
            contrat: trackingData.contrat,
        };
        if (keys.length > 1) {
            viewData.option = 'services_partages_tv_et_antispam_appel';
        } else if (keys.length === 1) {
            viewData.option =
                keys[0] === SharedServiceEnum.shareTv ? 'services_partages_tv' : 'services_partages_antispam_appel';
        }
        this.tmsService.trackView(viewData);
    }

    /**
     * Tracks view in TMS  for contract and options page
     */
    public trackContractOptionsView(): void {
        this.trackTmsView('selecteur_contrat', 'vos_contrats_espace_client_logue', '', 'offres_et_options');
    }

    /**
     * Tracks view in TMS  for bills page
     */
    public trackBillsView(): void {
        this.trackTmsView('selecteur_facture', 'factures_espace_client_logue', '', 'facture');
    }

    /**
     * Tracks view in TMS  for contract selector page
     */
    public trackContractSelectorView(): void {
        this.trackTmsView('selecteur', 'choix_retirer_un_contrat', 'VjD7Sa6l', 'retrait_contrat');
    }

    /**
     * Tracks view in TMS  for remove contract page
     * @param {string} pageType View type
     * @param {string} pageTitle View title
     * @param {string} id_tracking Tracking id
     */
    public trackRemoveContractView(pageType: string, pageTitle: string, id_tracking: string): void {
        const isSosh = this.portfolioFlagsService.isSosh();
        const viewData: any = {
            type_ec: isSosh ? 'Espace client Sosh' : 'Espace client Orange',
            couleur_contrat: isSosh ? 'Sosh' : 'Orange',
            type_page: pageType,
            titre_page: pageTitle,
            sous_univers: 'retrait_contrat',
            id_tracking: id_tracking,
            univers_affichage: 'dashboard',
            type_acte: 'NR',
        };
        this.tmsService.trackView(viewData);
    }

    /**
     * Track link click in TMS for remove contract page
     * @param {string} zone Link label
     * @param {string} label Link label
     * @param {string} targetUrl
     */
    public trackRemoveContractLink(zone: string, label: string, targetUrl: string): void {
        this.trackTmsLink('retirer_ce_contrat', zone, label, targetUrl);
    }

    /**
     * Track display suspended zone in TMS
     * @param {string} zone
     * @param {string} name
     * @param {string} targetUrl
     */
    public trackSuspendedZone(zone: string, name: string, targetUrl: string): void {
        this.trackTmsZone(zone, name, targetUrl);
    }

    /**
     * Tracks view in TMS
     * @param {string} pageType View type
     * @param {string} pageTitle View title
     * @param {string} idTracking
     * @param {string} subUnivers Sub-univers
     */
    public trackTmsView(pageType: string, pageTitle: string, idTracking: string, subUnivers: string): void {
        const isSosh = this.portfolioFlagsService.isSosh();
        const viewData: any = {
            type_ec: isSosh ? 'Espace client Sosh' : 'Espace client Orange',
            couleur_contrat: isSosh ? 'Sosh' : 'Orange',
            type_page: pageType,
            titre_page: pageTitle,
            id_tracking: idTracking,
            sous_univers: subUnivers,
            univers_affichage: 'global',
            type_acte: 'NR',
        };
        this.tmsService.trackView(viewData);
    }

    /**
     * Track link click in TMS
     * @param {string} page Page name
     * @param {string} zone Zone where link is displayed
     * @param {string} label Link label
     * @param {string} targetUrl
     */
    public trackTmsLink(page: string, zone: string, label: string, targetUrl: string): void {
        const linkData: any = {
            track_page: page,
            track_zone: zone,
            track_nom: TrackingService.formatLabel(label),
            track_cible: targetUrl,
            track_type_evt: 'click',
        };
        this.tmsService.trackLink(linkData);
    }

    /**
     * Track display zone in TMS
     * @param {string} zone
     * @param {string} name
     * @param {string} targetUrl
     */
    public trackTmsZone(zone: string, name: string, targetUrl: string): void {
        const zoneData: any = {
            track_zone: zone,
            track_nom: TrackingService.formatLabel(name),
            track_cible: targetUrl,
            nonInteraction: true,
        };
        this.tmsService.trackLink(zoneData);
    }

    /**
     * Format label for TMS (remove accent, replace space by underscore
     * @param {string} label
     * @return {string}
     */
    private static formatLabel(label: string): string {
        if (label) {
            let formattedLabel: string = label.toLowerCase();
            const nonAsciisValues = {
                a: '[àáâãäå]',
                c: 'ç',
                e: '[èéêë]',
                i: '[ìíîï]',
                n: 'ñ',
                o: '[òóôõö]',
                u: '[ùúûűü]',
                y: '[ýÿ]',
            };
            for (const i in nonAsciisValues) {
                if (Object.hasOwn(nonAsciisValues, i)) {
                    formattedLabel = formattedLabel.replace(new RegExp(nonAsciisValues[i], 'g'), i);
                }
            }
            return formattedLabel.split(' ').join('_');
        } else {
            return 'NR';
        }
    }

    public getTrackNamePerCategorie(categorie?: string, isMobile?: boolean, isSosh?: boolean): string {
        const trackingMap: Record<string, { sosh: string; orange: string; soshMob: string; orangeMob: string }> = {
            sim: {
                sosh: TmsCodes.care_digital_push_commercial_mobile_sosh,
                orange: TmsCodes.care_digital_push_commercial_mobile,
                soshMob: TmsCodes.care_digital_push_commercial_mobile_sosh_mob,
                orangeMob: TmsCodes.care_digital_push_commercial_mobile_mob,
            },
            home: {
                sosh: TmsCodes.care_digital_push_commercial_home_sosh,
                orange: TmsCodes.care_digital_push_commercial_home,
                soshMob: TmsCodes.care_digital_push_commercial_home_sosh_mob,
                orangeMob: TmsCodes.care_digital_push_commercial_home_mob,
            },
            entertainment: {
                sosh: TmsCodes.care_digital_push_commercial_divertissement_sosh,
                orange: TmsCodes.care_digital_push_commercial_divertissement,
                soshMob: TmsCodes.care_digital_push_commercial_divertissement_sosh_mob,
                orangeMob: TmsCodes.care_digital_push_commercial_divertissement_mob,
            },
        };

        return this.getTrackingName(categorie, isMobile, isSosh ?? false, trackingMap);
    }

    private isSectionV7ViewModel(element: TrackingElement): element is SectionV7ViewModel {
        return (
            !!element &&
            typeof element !== 'string' &&
            (element instanceof SectionV7ViewModel || 'changeOffer' in element)
        );
    }

    private isPushV7(element: TrackingElement): element is PushV7 {
        return !!element && typeof element !== 'string' && 'type' in element && !('changeOffer' in element);
    }

    public getTrackNamePerType(element: TrackingElement, isMobile?: boolean, isSosh?: boolean): string {
        // Gérer le cas où element est undefined
        if (!element) {
            return undefined;
        }

        const trackingMap: Record<string, { sosh: string; orange: string; soshMob: string; orangeMob: string }> = {
            warning: {
                sosh: TmsCodes.care_digital_push_top_alert_sosh,
                orange: TmsCodes.care_digital_push_top_alert,
                soshMob: TmsCodes.care_digital_push_top_alert_sosh_mob,
                orangeMob: TmsCodes.care_digital_push_top_alert_mob,
            },
            alert: {
                sosh: TmsCodes.care_digital_push_top_alert_sosh,
                orange: TmsCodes.care_digital_push_top_alert,
                soshMob: TmsCodes.care_digital_push_top_alert_sosh_mob,
                orangeMob: TmsCodes.care_digital_push_top_alert_mob,
            },
            info: {
                sosh: TmsCodes.care_digital_push_top_info_sosh,
                orange: TmsCodes.care_digital_push_top_info,
                soshMob: TmsCodes.care_digital_push_top_info_sosh_mob,
                orangeMob: TmsCodes.care_digital_push_top_info_mob,
            },
        };
        const type = this.extractTypeFromElement(element);

        if (type === 'change-offer' || type === 'change-mobile') {
            return this.extractIdRefZoneFromElement(element, type);
        } else {
            return this.getTrackingName(type, isMobile, isSosh, trackingMap);
        }
    }

    /**
     * Extrait le type d'un élément selon son type (SectionV7ViewModel, PushV7 ou string)
     */
    private extractTypeFromElement(element: TrackingElement): string {
        if (this.isSectionV7ViewModel(element)) {
            return element.changeOffer?.type;
        } else if (this.isPushV7(element)) {
            return element.type;
        } else {
            return element;
        }
    }

    /**
     * Extrait l'idRefZone d'un élément pour les types 'change-offer' ou 'change-mobile'
     */
    private extractIdRefZoneFromElement(element: TrackingElement, type: 'change-offer' | 'change-mobile'): string {
        if (this.isSectionV7ViewModel(element)) {
            return type === 'change-offer' ? element.changeOffer?.idRefZone : element.changeMobile?.idRefZone;
        } else if (this.isPushV7(element)) {
            return element.idRefZone;
        }
        return undefined;
    }

    private getTrackingName(
        key: string,
        isMobile: boolean,
        isSosh: boolean,
        trackingMap: Record<string, { sosh: string; orange: string; soshMob: string; orangeMob: string }>,
    ): string {
        const mapping = trackingMap[key];
        if (!mapping) {
            return undefined;
        }
        if (isMobile) {
            return isSosh ? mapping.soshMob : mapping.orangeMob;
        }
        return isSosh ? mapping.sosh : mapping.orange;
    }
}
